import React from 'react';
import { Box, Switch, FormControlLabel, TextField, Typography, Paper, RadioGroup, Radio, Tooltip, FormLabel } from '@mui/material';
import InfoTooltip from '../../InfoTooltip';

interface AdditionalOptionsProps {
  restoration: boolean;
  setRestoration: (v: boolean) => void;
  tdeEnabled: boolean;
  setTdeEnabled: (v: boolean) => void;
  tdeKeystorePath: string;
  setTdeKeystorePath: (v: string) => void;
  showKeystore: boolean;
  sourceTnsAdminEnabled: boolean;
  setSourceTnsAdminEnabled: (v: boolean) => void;
  sourceTnsAdminDir: string;
  setSourceTnsAdminDir: (v: string) => void;
  targetTnsAdminEnabled: boolean;
  setTargetTnsAdminEnabled: (v: boolean) => void;
  targetTnsAdminDir: string;
  setTargetTnsAdminDir: (v: string) => void;
  upgradeNodeEnabled: boolean;
  setUpgradeNodeEnabled: (v: boolean) => void;
  upgradeNodeValue: string;
  setUpgradeNodeValue: (v: string) => void;
  upgradeType: string;
  distributedUpgrade: boolean;
  setDistributedUpgrade: (v: boolean) => void;
  // Start Time (optional)
  startTimeType?: 'now' | 'delay' | 'datetime';
  setStartTimeType?: (v: 'now' | 'delay' | 'datetime') => void;
  startTimeValue?: string;
  setStartTimeValue?: (v: string) => void;
  platform: string;
  dropGrpAfterUpgrade: boolean;
  setDropGrpAfterUpgrade: (v: boolean) => void;
  timezoneUpgUpg?: boolean;
  setTimezoneUpgUpg?: (v: boolean) => void;
  runUtlrpUpg: boolean;
  setRunUtlrpUpg: (v: boolean) => void;
  removeUnderscoreUpg: boolean;
  setRemoveUnderscoreUpg: (v: boolean) => void;
  raiseCompatibleUpg: boolean;
  setRaiseCompatibleUpg: (v: boolean) => void;
  raiseCompatibleUpgValue: string;
  setRaiseCompatibleUpgValue: (v: string) => void;
  delDuringUpgradePfileEnabled: boolean;
  setDelDuringUpgradePfileEnabled: (v: boolean) => void;
  delDuringUpgradePfile: string;
  setDelDuringUpgradePfile: (v: string) => void;
  addAfterUpgradePfileEnabled: boolean;
  setAddAfterUpgradePfileEnabled: (v: boolean) => void;
  addAfterUpgradePfile: string;
  setAddAfterUpgradePfile: (v: string) => void;
  proactiveFixups?: boolean;
  setProactiveFixups?: (v: boolean) => void;
  delAfterUpgradePfileEnabled: boolean;
  setDelAfterUpgradePfileEnabled: (v: boolean) => void;
  delAfterUpgradePfile: string;
  setDelAfterUpgradePfile: (v: string) => void;
  addDuringUpgradePfileEnabled: boolean;
  setAddDuringUpgradePfileEnabled: (v: boolean) => void;
  addDuringUpgradePfile: string;
  setAddDuringUpgradePfile: (v: string) => void;
  emTargetNameEnabled: boolean;
  setEmTargetNameEnabled: (v: boolean) => void;
  emTargetName: string;
  setEmTargetName: (v: string) => void;
  emcliPathEnabled: boolean;
  setEmcliPathEnabled: (v: boolean) => void;
  emcliPath: string;
  setEmcliPath: (v: string) => void;
  emBlackoutSuffixEnabled: boolean;
  setEmBlackoutSuffixEnabled: (v: boolean) => void;
  emBlackoutSuffix: string;
  setEmBlackoutSuffix: (v: string) => void;
  oemSidPlaceholder: string;
}

const AdditionalOptions: React.FC<AdditionalOptionsProps> = ({
  restoration,
  setRestoration,
  tdeEnabled,
  setTdeEnabled,
  tdeKeystorePath,
  setTdeKeystorePath,
  showKeystore,
  sourceTnsAdminEnabled,
  setSourceTnsAdminEnabled,
  sourceTnsAdminDir,
  setSourceTnsAdminDir,
  targetTnsAdminEnabled,
  setTargetTnsAdminEnabled,
  targetTnsAdminDir,
  setTargetTnsAdminDir,
  upgradeNodeEnabled,
  setUpgradeNodeEnabled,
  upgradeNodeValue,
  setUpgradeNodeValue,
  upgradeType,
  distributedUpgrade,
  setDistributedUpgrade,
  startTimeType,
  setStartTimeType,
  startTimeValue,
  setStartTimeValue,
  platform,
  dropGrpAfterUpgrade,
  setDropGrpAfterUpgrade,
  timezoneUpgUpg,
  setTimezoneUpgUpg,
  runUtlrpUpg,
  setRunUtlrpUpg,
  removeUnderscoreUpg,
  setRemoveUnderscoreUpg,
  raiseCompatibleUpg,
  setRaiseCompatibleUpg,
  raiseCompatibleUpgValue,
  setRaiseCompatibleUpgValue,
  delDuringUpgradePfileEnabled,
  setDelDuringUpgradePfileEnabled,
  delDuringUpgradePfile,
  setDelDuringUpgradePfile,
  addAfterUpgradePfileEnabled,
  setAddAfterUpgradePfileEnabled,
  addAfterUpgradePfile,
  setAddAfterUpgradePfile,
  proactiveFixups,
  setProactiveFixups,
  delAfterUpgradePfileEnabled,
  setDelAfterUpgradePfileEnabled,
  delAfterUpgradePfile,
  setDelAfterUpgradePfile,
  addDuringUpgradePfileEnabled,
  setAddDuringUpgradePfileEnabled,
  addDuringUpgradePfile,
  setAddDuringUpgradePfile,
  emTargetNameEnabled,
  setEmTargetNameEnabled,
  emTargetName,
  setEmTargetName,
  emcliPathEnabled,
  setEmcliPathEnabled,
  emcliPath,
  setEmcliPath,
  emBlackoutSuffixEnabled,
  setEmBlackoutSuffixEnabled,
  emBlackoutSuffix,
  setEmBlackoutSuffix,
  oemSidPlaceholder,
}) => {
  const [dateWarning, setDateWarning] = React.useState('');
  // Touched states para campos editáveis
  const [sourceTnsAdminDirTouched, setSourceTnsAdminDirTouched] = React.useState(false);
  const [targetTnsAdminDirTouched, setTargetTnsAdminDirTouched] = React.useState(false);
  const [emTargetNameTouched, setEmTargetNameTouched] = React.useState(false);
  const [emcliPathTouched, setEmcliPathTouched] = React.useState(false);
  const [emBlackoutSuffixTouched, setEmBlackoutSuffixTouched] = React.useState(false);
  const [upgradeNodeValueTouched, setUpgradeNodeValueTouched] = React.useState(false);
  const [raiseCompatibleUpgValueTouched, setRaiseCompatibleUpgValueTouched] = React.useState(false);
  const [delDuringUpgradePfileTouched, setDelDuringUpgradePfileTouched] = React.useState(false);
  const [delAfterUpgradePfileTouched, setDelAfterUpgradePfileTouched] = React.useState(false);
  const [addDuringUpgradePfileTouched, setAddDuringUpgradePfileTouched] = React.useState(false);
  const [addAfterUpgradePfileTouched, setAddAfterUpgradePfileTouched] = React.useState(false);
  // Estado touched para Keystore Path
  const [tdeKeystorePathTouched, setTdeKeystorePathTouched] = React.useState(false);

  React.useEffect(() => {
    setSourceTnsAdminDirTouched(false);
    setTargetTnsAdminDirTouched(false);
    setEmTargetNameTouched(false);
    setEmcliPathTouched(false);
    setEmBlackoutSuffixTouched(false);
    setUpgradeNodeValueTouched(false);
    setRaiseCompatibleUpgValueTouched(false);
    setDelDuringUpgradePfileTouched(false);
    setDelAfterUpgradePfileTouched(false);
    setAddDuringUpgradePfileTouched(false);
    setAddAfterUpgradePfileTouched(false);
    setTdeKeystorePathTouched(false);
  }, [platform, upgradeType]);

  // Proactive Fixups and Distributed Upgrade logic
  const showProactiveFixups = upgradeType === 'regular';
  const showDistributedUpgrade = upgradeType === 'regular';
  const proactiveFixupsChecked = showProactiveFixups ? true : proactiveFixups;
  const proactiveFixupsDisabled = distributedUpgrade && showDistributedUpgrade;
  const showRestoration = upgradeType === 'regular';

  // Use 'now' as default if not set
  const effectiveStartTimeType = startTimeType ?? 'now';
  const showStartTime = effectiveStartTimeType && setStartTimeType && startTimeValue !== undefined && setStartTimeValue;

  // Get SID for placeholder
  const sid = (typeof window !== 'undefined' && (window as any).currentPatchSid) || 'DB1';
  const delDuringPlaceholder = platform === 'WINDOWS.X64'
    ? `C:\\Oracle\\AutoUpgrade\\${sid}_del_during.ora`
    : `/home/oracle/autoupgrade/${sid}_del_during.ora`;
  const addAfterPlaceholder = platform === 'WINDOWS.X64'
    ? `C:\\Oracle\\AutoUpgrade\\${sid}_add_after.ora`
    : `/home/oracle/autoupgrade/${sid}_add_after.ora`;
  const delAfterPlaceholder = platform === 'WINDOWS.X64'
    ? `C:\\Oracle\\AutoUpgrade\\${sid}_del_after.ora`
    : `/home/oracle/autoupgrade/${sid}_del_after.ora`;
  const addDuringPlaceholder = platform === 'WINDOWS.X64'
    ? `C:\\Oracle\\AutoUpgrade\\${sid}_add_during.ora`
    : `/home/oracle/autoupgrade/${sid}_add_during.ora`;

  function formatDateTimeInput(raw: string) {
    // Only digits, up to 14: ddmmyyyyhhmmss
    const v = raw.replace(/\D/g, '').slice(0, 14);
    let out = '';
    if (v.length > 0) out += v.slice(0, 2);
    if (v.length > 2) out += '/' + v.slice(2, 4);
    if (v.length > 4) out += '/' + v.slice(4, 8);
    if (v.length > 8) out += ' ' + v.slice(8, 10);
    if (v.length > 10) out += ':' + v.slice(10, 12);
    if (v.length > 12) out += ':' + v.slice(12, 14);
    return out;
  }

  // Validate date is not in the past
  React.useEffect(() => {
    if (effectiveStartTimeType === 'datetime' && startTimeValue && startTimeValue.length === 14) {
      const d = startTimeValue;
      const inputDate = new Date(
        Number(d.slice(4, 8)), // year
        Number(d.slice(2, 4)) - 1, // month (0-based)
        Number(d.slice(0, 2)), // day
        Number(d.slice(8, 10)), // hour
        Number(d.slice(10, 12)), // min
        Number(d.slice(12, 14)) // sec
      );
      if (inputDate.getTime() < Date.now()) {
        setDateWarning('Warning: Date cannot be in the past.');
      } else {
        setDateWarning('');
      }
    } else {
      setDateWarning('');
    }
  }, [effectiveStartTimeType, startTimeValue]);

  return (
    <Paper elevation={0} sx={{ p: 3, background: 'background.paper', borderRadius: 3 }}>
      {upgradeType === 'regular' ? (
        <Box>
          {/* First row: GRP and Custom TNS Admin */}
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, mb: 3 }}>
            <Paper elevation={0} sx={{ flex: 1, p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: theme => theme.palette.primary.main }}>GRP</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={restoration} onChange={e => setRestoration(e.target.checked)} />}
                  label={
                    <Tooltip
                      title="Generates a Guaranteed Restore Point (GRP) for database restoration."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Restoration</Typography>
                    </Tooltip>
                  }
                />
                <FormControlLabel
                  control={<Switch checked={dropGrpAfterUpgrade} onChange={e => setDropGrpAfterUpgrade(e.target.checked)} disabled={!restoration} />}
                  label={
                    <Tooltip
                      title="Drops the Guaranteed Restore Point after the upgrade is completed successfully."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Drop GRP After Upgrade</Typography>
                    </Tooltip>
                  }
                />
              </Box>
            </Paper>
            <Paper elevation={0} sx={{ flex: 1, p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: theme => theme.palette.primary.main }}>Custom TNS Admin</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={sourceTnsAdminEnabled} onChange={e => setSourceTnsAdminEnabled(e.target.checked)} />}
                  label={
                    <Tooltip
                      title="Specifies the path to the TNS_ADMIN directory in the source database home."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Custom Source TNS Admin</Typography>
                    </Tooltip>
                  }
                />
                {sourceTnsAdminEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    value={sourceTnsAdminDir}
                    onChange={e => { setSourceTnsAdminDirTouched(true); setSourceTnsAdminDir(e.target.value); }}
                    onFocus={() => setSourceTnsAdminDirTouched(true)}
                    placeholder={platform === 'WINDOWS.X64' ? 'C:\\Oracle\\Source\\TNS_ADMIN' : '/u01/app/source/tns_admin'}
                    sx={{ mt: 1, mb: 2 }}
                    InputProps={{
                      sx: {
                        '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& .MuiInputBase-input': {
                          color: !sourceTnsAdminDirTouched && sourceTnsAdminDir === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                        },
                        fontSize: 15,
                        background: 'transparent',
                      },
                    }}
                  />
                )}
                <FormControlLabel
                  control={<Switch checked={targetTnsAdminEnabled} onChange={e => setTargetTnsAdminEnabled(e.target.checked)} />}
                  label={
                    <Tooltip
                      title="Specifies the path to the TNS_ADMIN directory in the target database home."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Custom Target TNS Admin</Typography>
                    </Tooltip>
                  }
                />
                {targetTnsAdminEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    value={targetTnsAdminDir}
                    onChange={e => { setTargetTnsAdminDirTouched(true); setTargetTnsAdminDir(e.target.value); }}
                    onFocus={() => setTargetTnsAdminDirTouched(true)}
                    placeholder={platform === 'WINDOWS.X64' ? 'C:\\Oracle\\Target\\TNS_ADMIN' : '/u02/app/target/tns_admin'}
                    sx={{ mt: 1, mb: 2 }}
                    InputProps={{
                      sx: {
                        '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& .MuiInputBase-input': {
                          color: !targetTnsAdminDirTouched && targetTnsAdminDir === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                        },
                        fontSize: 15,
                        background: 'transparent',
                      },
                    }}
                  />
                )}
              </Box>
            </Paper>
          </Box>

          {/* Second row: RAC and OEM */}
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, mb: 3 }}>
            <Paper elevation={0} sx={{ flex: 1, p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: theme => theme.palette.primary.main }}>RAC</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={proactiveFixups} onChange={e => setProactiveFixups && setProactiveFixups(e.target.checked)} />}
                  label={
                    <Tooltip
                      title="Enables proactive fixups mode, where the PDBs are upgraded as the last stage of the upgrade. When the number of PDBs is higher than the CPU count defined in the database, divided by 2, choosing this tuning option can result in a faster upgrade."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Proactive Fixups</Typography>
                    </Tooltip>
                  }
                />
                <FormControlLabel
                  control={<Switch checked={distributedUpgrade} onChange={e => setDistributedUpgrade(e.target.checked)} />}
                  label={
                    <Tooltip
                      title="Specifies that AutoUpgrade performs a distributed upgrade. A distributed upgrade leverages the resources of the Oracle Clusterware cluster member nodes to perform the upgrades of PDBs more rapidly on the cluster. Use this option when a CDB in an Oracle RAC cluster of at least two nodes is being upgraded. When you choose this option, the proactive_fixups option is also enabled by default."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Distributed Upgrade</Typography>
                    </Tooltip>
                  }
                />
              </Box>
            </Paper>
            <Paper elevation={0} sx={{ flex: 1, p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme => theme.palette.primary.main }}>
                  OEM
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={emTargetNameEnabled} onChange={e => { setEmTargetNameEnabled(e.target.checked); if (!e.target.checked) setEmTargetName(''); }} />}
                  label={
                    <Tooltip
                      title="Enables you to specify that the database that you name is monitored by Enterprise Manager so the monitoring can be updated to the new Oracle home."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>EM Target Name</Typography>
                    </Tooltip>
                  }
                />
                {emTargetNameEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    value={emTargetName}
                    onChange={e => { setEmTargetNameTouched(true); setEmTargetName(e.target.value); }}
                    onFocus={() => setEmTargetNameTouched(true)}
                    placeholder={oemSidPlaceholder}
                    sx={{ mt: 1, mb: 2 }}
                    InputProps={{
                      sx: {
                        '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& .MuiInputBase-input': {
                          color: !emTargetNameTouched && emTargetName === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                        },
                        fontSize: 15,
                        background: 'transparent',
                      },
                    }}
                  />
                )}
                <FormControlLabel
                  control={<Switch checked={emcliPathEnabled} onChange={e => { setEmcliPathEnabled(e.target.checked); if (!e.target.checked) setEmcliPath(''); }} />}
                  label={
                    <Tooltip
                      title="Enables you to specify the path to the Enterprise Manager Command Line Interface (EMCLI) command."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>EMCLI Path</Typography>
                    </Tooltip>
                  }
                />
                {emcliPathEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    value={emcliPath}
                    onChange={e => { setEmcliPathTouched(true); setEmcliPath(e.target.value); }}
                    onFocus={() => setEmcliPathTouched(true)}
                    placeholder={platform === 'WINDOWS.X64' ? 'C:\\Oracle\\emcli\\bin\\emcli' : '/u01/app/oracle/emcli/bin/emcli'}
                    sx={{ mt: 1, mb: 2 }}
                    InputProps={{
                      sx: {
                        '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& .MuiInputBase-input': {
                          color: !emcliPathTouched && emcliPath === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                        },
                        fontSize: 15,
                        background: 'transparent',
                      },
                    }}
                  />
                )}
                <FormControlLabel
                  control={<Switch checked={emBlackoutSuffixEnabled} onChange={e => { setEmBlackoutSuffixEnabled(e.target.checked); if (!e.target.checked) setEmBlackoutSuffix(''); }} />}
                  label={
                    <Tooltip
                      title="Enables you to specify a suffix to add to the default autoupgrade blackout"
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>EM Blackout Suffix</Typography>
                    </Tooltip>
                  }
                />
                {emBlackoutSuffixEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    value={emBlackoutSuffix}
                    onChange={e => { setEmBlackoutSuffixTouched(true); setEmBlackoutSuffix(e.target.value); }}
                    onFocus={() => setEmBlackoutSuffixTouched(true)}
                    placeholder={platform === 'WINDOWS.X64' ? 'em_blackout_AutoUpgrade_DB1' : 'em_blackout_AutoUpgrade_DB1'}
                    sx={{ mt: 1, mb: 2 }}
                    InputProps={{
                      sx: {
                        '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& .MuiInputBase-input': {
                          color: !emBlackoutSuffixTouched && emBlackoutSuffix === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                        },
                        fontSize: 15,
                        background: 'transparent',
                      },
                    }}
                  />
                )}
              </Box>
            </Paper>
          </Box>

          {/* Third row: Additional Params and Start Time */}
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, mb: 3 }}>
            <Paper elevation={0} sx={{ flex: 1, p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: theme => theme.palette.primary.main }}>Additional Params</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={runUtlrpUpg} onChange={e => setRunUtlrpUpg(e.target.checked)} />}
                  label={
                    <Tooltip
                      title="Runs utlrp.sql after the upgrade to recompile invalid objects."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Run utlrp.sql</Typography>
                    </Tooltip>
                  }
                />
                <FormControlLabel
                  control={<Switch checked={!!timezoneUpgUpg} onChange={e => setTimezoneUpgUpg && setTimezoneUpgUpg(e.target.checked)} />}
                  label={
                    <Tooltip
                      title="Performs timezone upgrade during the database upgrade process."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Timezone Upgrade</Typography>
                    </Tooltip>
                  }
                />
                <FormControlLabel
                  control={<Switch checked={upgradeNodeEnabled} onChange={e => setUpgradeNodeEnabled(e.target.checked)} />}
                  label={
                    <Tooltip
                      title="Specifies the node on which the current user configuration is valid. The default value is localhost."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Upgrade Node</Typography>
                    </Tooltip>
                  }
                />
                {upgradeNodeEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    value={upgradeNodeValue}
                    onChange={e => { setUpgradeNodeValueTouched(true); setUpgradeNodeValue(e.target.value); }}
                    onFocus={() => setUpgradeNodeValueTouched(true)}
                    placeholder="myhost01"
                    sx={{ mt: 1, mb: 2 }}
                    InputProps={{
                      sx: {
                        '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& .MuiInputBase-input': {
                          color: !upgradeNodeValueTouched && upgradeNodeValue === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                        },
                        fontSize: 15,
                        background: 'transparent',
                      },
                    }}
                  />
                )}
                <FormControlLabel
                  control={<Switch checked={raiseCompatibleUpg} onChange={e => setRaiseCompatibleUpg(e.target.checked)} />}
                  label={
                    <Tooltip
                      title="Raises the compatible parameter to the target version after the upgrade."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Raise Compatible</Typography>
                    </Tooltip>
                  }
                />
                {raiseCompatibleUpg && (
                  <TextField
                    fullWidth
                    size="small"
                    value={raiseCompatibleUpgValue}
                    onChange={e => { setRaiseCompatibleUpgValueTouched(true); setRaiseCompatibleUpgValue(e.target.value); }}
                    onFocus={() => setRaiseCompatibleUpgValueTouched(true)}
                    placeholder="Eg. 23.5.0"
                    sx={{ mt: 1, mb: 2 }}
                    InputProps={{
                      sx: {
                        '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& .MuiInputBase-input': {
                          color: !raiseCompatibleUpgValueTouched && raiseCompatibleUpgValue === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                        },
                        fontSize: 15,
                        background: 'transparent',
                      },
                    }}
                  />
                )}
              </Box>
            </Paper>
            <Paper elevation={0} sx={{ flex: 1, p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme => theme.palette.primary.main }}>
                  Start Time
                </Typography>
                <InfoTooltip
                  title="Sets a future start time for the upgrade job to run. Use this parameter to schedule upgrade jobs to balance the load on your server, and to prevent multiple jobs from starting immediately."
                  placement="right"
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <RadioGroup
                  value={effectiveStartTimeType}
                  onChange={e => {
                    const newType = e.target.value as 'now' | 'delay' | 'datetime';
                    (setStartTimeType || (() => {}))(newType);
                    if ((setStartTimeValue || (() => {})) && (newType === 'delay' || newType === 'datetime')) {
                      (setStartTimeValue || (() => {}))('');
                    }
                  }}
                >
                  <FormControlLabel value="now" control={<Radio />} label="Now" />
                  <FormControlLabel value="delay" control={<Radio />} label={'+XhYm'} />
                  <FormControlLabel value="datetime" control={<Radio />} label="dd/mm/yyyy hh:mm:ss" />
                </RadioGroup>
                {effectiveStartTimeType === 'delay' && (
                  <TextField
                    size="small"
                    label="Delay (e.g. +2h30m)"
                    value={startTimeValue}
                    onChange={e => setStartTimeValue && setStartTimeValue(e.target.value)}
                    placeholder="+1h0m"
                    sx={{ mt: 1, width: '100%' }}
                  />
                )}
                {effectiveStartTimeType === 'datetime' && (
                  <>
                    <TextField
                      size="small"
                      label="Date & Time (dd/mm/yyyy hh:mm:ss)"
                      value={formatDateTimeInput(startTimeValue || '')}
                      onChange={e => {
                        const raw = e.target.value.replace(/\D/g, '');
                        setStartTimeValue && setStartTimeValue(raw);
                      }}
                      placeholder="10/05/2025 14:30:00"
                      sx={{ mt: 1, width: '100%' }}
                      error={!!dateWarning}
                    />
                    {dateWarning && (
                      <Typography color="error" sx={{ mt: 1, fontSize: 14 }}>{dateWarning}</Typography>
                    )}
                  </>
                )}
              </Box>
            </Paper>
          </Box>

          {/* Fourth row: PFILE/SPFILE */}
          <Paper elevation={0} sx={{ mb: 3, p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: theme => theme.palette.primary.main }}>PFILE/SPFILE</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={<Switch checked={removeUnderscoreUpg} onChange={e => setRemoveUnderscoreUpg(e.target.checked)} />}
                label={
                  <Tooltip
                    title="Removes underscore parameters from the database after the upgrade."
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 320,
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>Remove Underscore Parameters</Typography>
                  </Tooltip>
                }
              />
              <FormControlLabel
                control={<Switch checked={delDuringUpgradePfileEnabled} onChange={e => { setDelDuringUpgradePfileEnabled(e.target.checked); if (!e.target.checked) { setDelDuringUpgradePfile(''); } }} />}
                label={
                  <Tooltip
                    title="Specifies a PFILE to be deleted during the upgrade process."
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 320,
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>Del During Upgrade PFILE</Typography>
                  </Tooltip>
                }
              />
              {delDuringUpgradePfileEnabled && (
                <TextField
                  fullWidth
                  size="small"
                  value={delDuringUpgradePfile}
                  onChange={e => { setDelDuringUpgradePfileTouched(true); setDelDuringUpgradePfile(e.target.value); }}
                  onFocus={() => setDelDuringUpgradePfileTouched(true)}
                  placeholder={delDuringPlaceholder}
                  sx={{ mt: 1, mb: 2 }}
                  InputProps={{
                    sx: {
                      '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                      '& .MuiInputBase-input': {
                        color: !delDuringUpgradePfileTouched && delDuringUpgradePfile === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                      },
                      fontSize: 15,
                      background: 'transparent',
                    },
                  }}
                />
              )}
              <FormControlLabel
                control={<Switch checked={delAfterUpgradePfileEnabled} onChange={e => { setDelAfterUpgradePfileEnabled(e.target.checked); if (!e.target.checked) { setDelAfterUpgradePfile(''); } }} />}
                label={
                  <Tooltip
                    title="Specifies a PFILE to be deleted after the upgrade is completed."
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 320,
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>Del After Upgrade PFILE</Typography>
                  </Tooltip>
                }
              />
              {delAfterUpgradePfileEnabled && (
                <TextField
                  fullWidth
                  size="small"
                  value={delAfterUpgradePfile}
                  onChange={e => { setDelAfterUpgradePfileTouched(true); setDelAfterUpgradePfile(e.target.value); }}
                  onFocus={() => setDelAfterUpgradePfileTouched(true)}
                  placeholder={delAfterPlaceholder}
                  sx={{ mt: 1, mb: 2 }}
                  InputProps={{
                    sx: {
                      '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                      '& .MuiInputBase-input': {
                        color: !delAfterUpgradePfileTouched && delAfterUpgradePfile === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                      },
                      fontSize: 15,
                      background: 'transparent',
                    },
                  }}
                />
              )}
              <FormControlLabel
                control={<Switch checked={addDuringUpgradePfileEnabled} onChange={e => { setAddDuringUpgradePfileEnabled(e.target.checked); if (!e.target.checked) { setAddDuringUpgradePfile(''); } }} />}
                label={
                  <Tooltip
                    title="Specifies a PFILE to be added during the upgrade process."
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 320,
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>Add During Upgrade PFILE</Typography>
                  </Tooltip>
                }
              />
              {addDuringUpgradePfileEnabled && (
                <TextField
                  fullWidth
                  size="small"
                  value={addDuringUpgradePfile}
                  onChange={e => { setAddDuringUpgradePfileTouched(true); setAddDuringUpgradePfile(e.target.value); }}
                  onFocus={() => setAddDuringUpgradePfileTouched(true)}
                  placeholder={addDuringPlaceholder}
                  sx={{ mt: 1, mb: 2 }}
                  InputProps={{
                    sx: {
                      '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                      '& .MuiInputBase-input': {
                        color: !addDuringUpgradePfileTouched && addDuringUpgradePfile === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                      },
                      fontSize: 15,
                      background: 'transparent',
                    },
                  }}
                />
              )}
              <FormControlLabel
                control={<Switch checked={addAfterUpgradePfileEnabled} onChange={e => { setAddAfterUpgradePfileEnabled(e.target.checked); if (!e.target.checked) { setAddAfterUpgradePfile(''); } }} />}
                label={
                  <Tooltip
                    title="Specifies a PFILE to be added after the upgrade is completed."
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 320,
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>Add After Upgrade PFILE</Typography>
                  </Tooltip>
                }
              />
              {addAfterUpgradePfileEnabled && (
                <TextField
                  fullWidth
                  size="small"
                  value={addAfterUpgradePfile}
                  onChange={e => { setAddAfterUpgradePfileTouched(true); setAddAfterUpgradePfile(e.target.value); }}
                  onFocus={() => setAddAfterUpgradePfileTouched(true)}
                  placeholder={addAfterPlaceholder}
                  sx={{ mt: 1, mb: 2 }}
                  InputProps={{
                    sx: {
                      '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                      '& .MuiInputBase-input': {
                        color: !addAfterUpgradePfileTouched && addAfterUpgradePfile === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                      },
                      fontSize: 15,
                      background: 'transparent',
                    },
                  }}
                />
              )}
            </Box>
          </Paper>
        </Box>
      ) : null}

      {(upgradeType === 'noncdb2cdb' || upgradeType === 'pdbrelocation') ? (
        <>
          <Box>
            <FormControlLabel
              control={<Switch checked={tdeEnabled} onChange={e => setTdeEnabled(e.target.checked)} />}
              label={<Typography sx={{ fontWeight: 600 }}>Database has TDE enabled</Typography>}
            />
          </Box>
          <Box>
            <FormControlLabel
              control={<Switch checked={sourceTnsAdminEnabled} onChange={e => setSourceTnsAdminEnabled(e.target.checked)} />}
              label={<Typography sx={{ fontWeight: 600 }}>Custom Source TNS Admin</Typography>}
            />
            {sourceTnsAdminEnabled && (
              <TextField
                fullWidth
                size="small"
                value={sourceTnsAdminDir}
                onChange={e => { setSourceTnsAdminDirTouched(true); setSourceTnsAdminDir(e.target.value); }}
                onFocus={() => setSourceTnsAdminDirTouched(true)}
                placeholder={platform === 'WINDOWS.X64' ? 'C:\\Oracle\\Source\\TNS_ADMIN' : '/u01/app/source/tns_admin'}
                sx={{ mt: 1, mb: 2 }}
                InputProps={{
                  sx: {
                    '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                    '& .MuiInputBase-input': {
                      color: !sourceTnsAdminDirTouched && sourceTnsAdminDir === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    fontSize: 15,
                    background: 'transparent',
                  },
                }}
              />
            )}
          </Box>
          <Box>
            <FormControlLabel
              control={<Switch checked={targetTnsAdminEnabled} onChange={e => setTargetTnsAdminEnabled(e.target.checked)} />}
              label={<Typography sx={{ fontWeight: 600 }}>Custom Target TNS Admin</Typography>}
            />
            {targetTnsAdminEnabled && (
              <TextField
                fullWidth
                size="small"
                value={targetTnsAdminDir}
                onChange={e => { setTargetTnsAdminDirTouched(true); setTargetTnsAdminDir(e.target.value); }}
                onFocus={() => setTargetTnsAdminDirTouched(true)}
                placeholder={platform === 'WINDOWS.X64' ? 'C:\\Oracle\\Target\\TNS_ADMIN' : '/u02/app/target/tns_admin'}
                sx={{ mt: 1, mb: 2 }}
                InputProps={{
                  sx: {
                    '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                    '& .MuiInputBase-input': {
                      color: !targetTnsAdminDirTouched && targetTnsAdminDir === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    fontSize: 15,
                    background: 'transparent',
                  },
                }}
              />
            )}
          </Box>
          {/* Start Time below TNS Admin switches */}
          <Box sx={{ mt: 2 }}>
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 600, display: 'inline' }}>Start Time:</Typography>
              <InfoTooltip
                title="Sets a future start time for the upgrade job to run. Use this parameter to schedule upgrade jobs to balance the load on your server, and to prevent multiple jobs from starting immediately."
                placement="right"
              />
            </FormLabel>
            <RadioGroup
              name="start-time-type-additional"
              value={startTimeType}
              onChange={e => (setStartTimeType ? setStartTimeType(e.target.value as 'now' | 'delay' | 'datetime') : undefined)}
            >
              <FormControlLabel value="now" control={<Radio />} label="Now" />
              <FormControlLabel value="delay" control={<Radio />} label={'+XhYm (delay after launch)'} />
              <FormControlLabel value="datetime" control={<Radio />} label="dd/mm/yyyy hh:mm:ss" />
            </RadioGroup>
            {startTimeType === 'delay' && (
              <TextField
                size="small"
                label="Delay (e.g. +2h30m)"
                value={startTimeValue}
                onChange={e => (setStartTimeValue ? setStartTimeValue(e.target.value) : undefined)}
                placeholder="+1h0m"
                sx={{ mt: 1, width: '100%' }}
              />
            )}
            {startTimeType === 'datetime' && (
              <TextField
                size="small"
                label="Date & Time (dd/mm/yyyy hh:mm:ss)"
                value={formatDateTimeInput(startTimeValue || '')}
                onChange={e => {
                  const raw = e.target.value.replace(/\D/g, '').slice(0, 14);
                  if (setStartTimeValue) setStartTimeValue(raw);
                }}
                placeholder="10/05/2025 14:30:00"
                sx={{ mt: 1, width: '100%' }}
                error={!!dateWarning}
                helperText={dateWarning}
              />
            )}
          </Box>
          {showKeystore && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Keystore Path:</Typography>
              <TextField
                fullWidth
                size="small"
                value={tdeKeystorePath}
                onChange={e => { setTdeKeystorePathTouched(true); setTdeKeystorePath(e.target.value); }}
                onFocus={() => setTdeKeystorePathTouched(true)}
                placeholder={platform === 'WINDOWS.X64' ? 'C:\\Oracle\\AutoUpgrade\\Keystore' : '/home/oracle/autoupgrade/keystore'}
                InputProps={{
                  sx: {
                    '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                    '& .MuiInputBase-input': {
                      color: !tdeKeystorePathTouched && tdeKeystorePath === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    fontSize: 15,
                    background: 'transparent',
                  },
                }}
              />
            </Box>
          )}
        </>
      ) : null}

      {/* Fallback: original single-column layout if no start time props */}
      {!showStartTime && (
        <>
          {showRestoration && (
            <Box>
              <FormControlLabel
                control={<Switch checked={restoration} onChange={e => setRestoration(e.target.checked)} />}
                label={
                  <Tooltip
                    title="Generates a Guaranteed Restore Point (GRP) for database restoration."
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 320,
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>Restoration</Typography>
                  </Tooltip>
                }
              />
            </Box>
          )}
          <Box>
            <FormControlLabel
              control={<Switch checked={tdeEnabled} onChange={e => setTdeEnabled(e.target.checked)} />}
              label={<Typography sx={{ fontWeight: 600 }}>Database has TDE enabled</Typography>}
            />
          </Box>
          {showKeystore && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Keystore Path:</Typography>
              <TextField
                fullWidth
                size="small"
                value={tdeKeystorePath}
                onChange={e => { setTdeKeystorePathTouched(true); setTdeKeystorePath(e.target.value); }}
                onFocus={() => setTdeKeystorePathTouched(true)}
                placeholder={platform === 'WINDOWS.X64' ? 'C:\\Oracle\\AutoUpgrade\\Keystore' : '/home/oracle/autoupgrade/keystore'}
                InputProps={{
                  sx: {
                    '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                    '& .MuiInputBase-input': {
                      color: !tdeKeystorePathTouched && tdeKeystorePath === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    fontSize: 15,
                    background: 'transparent',
                  },
                }}
              />
            </Box>
          )}
          <Box>
            <FormControlLabel
              control={<Switch checked={sourceTnsAdminEnabled} onChange={e => setSourceTnsAdminEnabled(e.target.checked)} />}
              label={
                <Tooltip
                  title="Specifies the path to the TNS_ADMIN directory in the source database home."
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                        color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                        fontSize: 15,
                        borderRadius: 2,
                        boxShadow: 3,
                        px: 2,
                        py: 1,
                        fontWeight: 400,
                        minWidth: 320,
                      },
                    },
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>Custom Source TNS Admin</Typography>
                </Tooltip>
              }
            />
            {sourceTnsAdminEnabled && (
              <TextField
                fullWidth
                size="small"
                value={sourceTnsAdminDir}
                onChange={e => { setSourceTnsAdminDirTouched(true); setSourceTnsAdminDir(e.target.value); }}
                onFocus={() => setSourceTnsAdminDirTouched(true)}
                placeholder={platform === 'WINDOWS.X64' ? 'C:\\Oracle\\Source\\TNS_ADMIN' : '/u01/app/source/tns_admin'}
                sx={{ mt: 1, mb: 2 }}
                InputProps={{
                  sx: {
                    '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                    '& .MuiInputBase-input': {
                      color: !sourceTnsAdminDirTouched && sourceTnsAdminDir === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    fontSize: 15,
                    background: 'transparent',
                  },
                }}
              />
            )}
          </Box>
          <Box>
            <FormControlLabel
              control={<Switch checked={targetTnsAdminEnabled} onChange={e => setTargetTnsAdminEnabled(e.target.checked)} />}
              label={
                <Tooltip
                  title="Specifies the path to the TNS_ADMIN directory in the target database home."
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                        color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                        fontSize: 15,
                        borderRadius: 2,
                        boxShadow: 3,
                        px: 2,
                        py: 1,
                        fontWeight: 400,
                        minWidth: 320,
                      },
                    },
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>Custom Target TNS Admin</Typography>
                </Tooltip>
              }
            />
            {targetTnsAdminEnabled && (
              <TextField
                fullWidth
                size="small"
                value={targetTnsAdminDir}
                onChange={e => { setTargetTnsAdminDirTouched(true); setTargetTnsAdminDir(e.target.value); }}
                onFocus={() => setTargetTnsAdminDirTouched(true)}
                placeholder={platform === 'WINDOWS.X64' ? 'C:\\Oracle\\Target\\TNS_ADMIN' : '/u02/app/target/tns_admin'}
                sx={{ mt: 1, mb: 2 }}
                InputProps={{
                  sx: {
                    '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                    '& .MuiInputBase-input': {
                      color: !targetTnsAdminDirTouched && targetTnsAdminDir === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    fontSize: 15,
                    background: 'transparent',
                  },
                }}
              />
            )}
          </Box>
          {showProactiveFixups && (
            <Box>
              <FormControlLabel
                control={<Switch checked={proactiveFixupsChecked} disabled={proactiveFixupsDisabled} onChange={e => setProactiveFixups && setProactiveFixups(e.target.checked)} />}
                label={
                  <Tooltip
                    title="Enables proactive fixups mode, where the PDBs are upgraded as the last stage of the upgrade. When the number of PDBs is higher than the CPU count defined in the database, divided by 2, choosing this tuning option can result in a faster upgrade."
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 320,
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>Proactive Fixups</Typography>
                  </Tooltip>
                }
              />
            </Box>
          )}
          {showDistributedUpgrade && (
            <Box>
              <FormControlLabel
                control={<Switch checked={distributedUpgrade} onChange={e => {
                  setDistributedUpgrade(e.target.checked);
                  if (e.target.checked) setProactiveFixups && setProactiveFixups(true);
                }} />}
                label={
                  <Tooltip
                    title={
                      <>
                        Specifies that AutoUpgrade performs a <strong>distributed upgrade</strong>. A distributed upgrade leverages the resources of the Oracle Clusterware cluster member nodes to perform the upgrades of PDBs more rapidly on the cluster. Use this option when a CDB in an Oracle RAC cluster of at least two nodes is being upgraded. When you choose this option, the proactive_fixups option is also enabled by default.
                      </>
                    }
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 320,
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>Distributed Upgrade (RAC)</Typography>
                  </Tooltip>
                }
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default AdditionalOptions; 